import type { FlowConfig } from '../types'

// SER-5976 (F2): everything a flow save needs to know about KB links, extracted pure so the
// anti-wipe rule is unit-tested (kb-links-for-save.test.ts) instead of buried in usePublishFlow.
export interface KbLinksForSave {
  // flowData.phlo with `knowledge_base_ids` (controlled explicitly via kbLinksPayloadPart) and the
  // response-only `knowledge_base_ids_unavailable` marker (must never be echoed back) stripped, so
  // neither is carried into the payload spread unintentionally.
  phloBaseForPayload: Omit<FlowConfig, 'knowledge_base_ids' | 'knowledge_base_ids_unavailable'>
  // The resolved set the save sends (also mirrored into the agent-settings normalization).
  normalizedKnowledgeBaseIds: string[]
  // Spread into the phlo payload: `{ knowledge_base_ids }` normally; `{}` (omit-to-preserve) when
  // the links are UNKNOWN — so PHLO-core keeps its stored set instead of replace-all-ing with a
  // transient-empty [].
  kbLinksPayloadPart: { knowledge_base_ids?: string[] }
  // True when the links are UNKNOWN (the F2 guard tripped: marker set + no explicit selection).
  // Surfaced as a single source of truth so BOTH the payload (kbLinksPayloadPart omits the ids) AND
  // the post-save agent-settings STORE SYNC (kbIdsForStoreSync preserves the existing ids) branch
  // off the SAME condition, instead of recomputing it divergently — see kbIdsForStoreSync.
  preserveExistingKbLinks: boolean
}

// KB links are owned by the dedicated link/unlink mutation (useLinkKnowledgeBaseMutation): it
// PERSISTS the change and invalidates flowData, so phlo.knowledge_base_ids is the fresh backend
// truth. The general flow publish must only PRESERVE that set — never overwrite it from the
// client-side agentSettings cache. agentSettings is initialized once per flow load and drifts; when
// it was stale (missing a KB linked earlier — e.g. a slow-ingesting crawl KB still being attached)
// it became the top non-explicit source here and the backend replace-all SILENTLY DROPPED the link.
// Prefer an explicit selection, then the persisted backend value, and only fall back to the cache
// if flowData carries no list at all. (?? not || so a real empty [] from the backend is respected.)
//
// SER-5976 (F2): the phlo/flow response flags its knowledge_base_ids UNKNOWN — not empty — when the
// backend couldn't resolve the linked KB uuids (config #345 `knowledge_base_ids_unavailable`,
// mirroring the reverse-read's null/linksUnavailable contract). The preserve-from-backend ordering
// above can't save us here: with the links flagged unknown, phlo.knowledge_base_ids is a
// null/transient-empty placeholder, so `??` falls THROUGH to the agentSettings cache — and a
// replace-all with whatever that drifting cache holds would WIPE every link the console never saw
// during the outage.
//
// So the guard rests on TWO conditions ALONE: the links are flagged unknown AND the user made no
// explicit link/unlink selection this session (selectedKBs === undefined). When both hold we simply
// do not know the real link set — REGARDLESS of what normalizedKnowledgeBaseIds resolved to — so we
// OMIT knowledge_base_ids from the payload (kbLinksPayloadPart = {}) and PHLO-core preserves
// whatever it already has (the omit-to-preserve convention). We deliberately do NOT also require an
// empty resolved set: the earlier `normalizedKnowledgeBaseIds.length === 0` conjunct DEFEATED the
// guard — when knowledge_base_ids is a nullish placeholder, `??` resolves to the agentSettings
// cache, and if the user touched ANY KB this session that cache is non-empty, so the length check
// was false and the stale session subset leaked through into a replace-all. An EXPLICIT selection —
// including an explicit [] (unlink-all) — always overrides and goes through unchanged.
export const kbLinksForSave = (
  phlo: FlowConfig,
  selectedKBs: string[] | undefined,
  agentSettingsKbIds: string[] | undefined,
): KbLinksForSave => {
  const {
    knowledge_base_ids: _spreadKbIds,
    knowledge_base_ids_unavailable: _spreadKbUnavailable,
    ...phloBaseForPayload
  } = phlo
  const normalizedKnowledgeBaseIds =
    selectedKBs ?? phlo.knowledge_base_ids ?? agentSettingsKbIds ?? []
  const kbLinksUnknown = !!phlo.knowledge_base_ids_unavailable && selectedKBs === undefined
  return {
    phloBaseForPayload,
    normalizedKnowledgeBaseIds,
    kbLinksPayloadPart: kbLinksUnknown ? {} : { knowledge_base_ids: normalizedKnowledgeBaseIds },
    preserveExistingKbLinks: kbLinksUnknown,
  }
}

// Which KB-id list the post-save agent-settings STORE SYNC (usePublishFlow → setAgentSettings)
// should write. Normally it's the same normalized set the payload sent. But on an unknown-links save
// the payload OMITTED knowledge_base_ids (preserveExistingKbLinks) so PHLO-core kept its stored set —
// so the store must ALSO be left as-is (return the ids the store ALREADY had), or the local UI would
// flip to the stale/[] normalized set and disagree with the correctly-preserved server links until
// the flowData refetch lands (SER-5976 F2, review #3447). Same signal drives both branches — no
// divergent recompute. Falls back to the normalized set when the store has no ids yet (best-effort:
// there is nothing to preserve).
export const kbIdsForStoreSync = (
  links: Pick<KbLinksForSave, 'normalizedKnowledgeBaseIds' | 'preserveExistingKbLinks'>,
  existingStoreKbIds: string[] | undefined,
): string[] =>
  links.preserveExistingKbLinks
    ? (existingStoreKbIds ?? links.normalizedKnowledgeBaseIds)
    : links.normalizedKnowledgeBaseIds
